import React, { useState, useCallback, useRef } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { ArticleJsonLd } from '../components/seo/SchemaMarkup';
import { ReadingProgressBar } from '../components/articles/ReadingProgressBar';
import GymDirectoryLayout from '../components/articles/GymDirectoryLayout';
import { useReadingProgress } from '../lib/useReadingProgress';
import { ArticleLoadingView, ArticleNotFoundView } from './article/ArticleFallbackViews';
import { ArticleHero, ArticleHeader, BackToBlogLink } from './article/ArticleHero';
import { ArticleBody } from './article/ArticleBody';
import { ArticleSidebar } from './article/ArticleSidebar';
import { ArticleFooterPopups } from './article/ArticleFooterPopups';
import { ArticleStyles } from './article/ArticleStyles';
import { CustomArticleView } from './article/CustomArticleView';
import { StandaloneArticleView } from './article/StandaloneArticleView';
import { hasCustomLayout } from './article/customArticleMap';
import { useFetchArticle } from './article/useFetchArticle';
import { ArticlePartnerOffers } from '../components/partners/ArticlePartnerOffers';
import AprStickyRail from '../components/apr/AprStickyRail';
import {
  useArticleMetaTags,
  useArticlePopupTriggers,
  useHyroxStickyBar,
  useInlineMagnetForms,
  useCadenceCalculator,
  useRecoveryScorecard,
  useScrollDepthTracking,
  useHideBrokenImages,
} from './article/useArticleEffects';
import { articleUrlFor } from './article/articleHelpers';

export default function ArticlePage() {
  const { slug } = useParams();
  const [showPopup, setShowPopup] = useState(false);
  const contentRef = useRef(null);
  const standaloneRef = useRef(null);
  const { saveProgress } = useReadingProgress();

  const { article, loading, suggestedArticles } = useFetchArticle(slug, saveProgress);

  const handleProgressChange = useCallback((pct) => {
    if (!article) return;
    saveProgress(slug, {
      title: article.title,
      category: article.category,
      featured_image: article.featured_image,
      read_time: article.read_time,
      progress: pct,
    });
  }, [slug, saveProgress, article]);

  useArticleMetaTags(article);
  useArticlePopupTriggers(article, contentRef, setShowPopup);
  useInlineMagnetForms(article);
  useCadenceCalculator(article);
  useRecoveryScorecard(article);
  useScrollDepthTracking(article);
  useHideBrokenImages(article, contentRef);
  const stickyBar = useHyroxStickyBar(article);

  if (loading) return <ArticleLoadingView />;
  if (!article) return <ArticleNotFoundView suggestedArticles={suggestedArticles} />;

  // Slug-alias 301-style redirect: a consolidated/merged page resolves to its
  // canonical slug — send the browser to the canonical URL.
  if (article.slug && article.slug !== slug) {
    return <Navigate to={`/blogs/article/${article.slug}`} replace />;
  }

  // Bespoke interactive articles (peptide side effects, wolverine complex, etc.)
  if (hasCustomLayout(slug)) {
    return <CustomArticleView slug={slug} article={article} />;
  }

  // Self-contained HTML articles (richer layout, ship raw HTML)
  if (article.layout === 'standalone') {
    return <StandaloneArticleView article={article} slug={slug} standaloneRef={standaloneRef} />;
  }

  const articleUrl = articleUrlFor(slug);

  return (
    <main className="pt-20 min-h-screen" data-testid="article-page">
      <ReadingProgressBar contentRef={contentRef} onProgressChange={handleProgressChange} />
      <ArticleJsonLd article={article} slug={slug} />

      <ArticleHero article={article} />

      <article className="container-ninja -mt-32 relative z-10 pb-24">
        <BackToBlogLink />
        <ArticleHeader article={article} />

        {article.gyms && article.gyms.length > 0 ? (
          <><GymDirectoryLayout article={article} /><ArticlePartnerOffers article={article} placement="directory-end"/></>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8">
              <ArticleBody article={article} slug={slug} contentRef={contentRef} />
            </div>
            <ArticleSidebar article={article} slug={slug} articleUrl={articleUrl} onShowProgramPopup={() => setShowPopup(true)} />
          </div>
        )}
      </article>

      <ArticleStyles />

      {article.category === 'Peptides' && <AprStickyRail article={article} />}

      <ArticleFooterPopups article={article} slug={slug} showPopup={showPopup} stickyBar={stickyBar} />
    </main>
  );
}
